"use client";
import type { Media, OmniboxDemo } from "@mw/shared/payload-types";

import { EventName } from "@mw/shared/analyticsEventNames";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { cn } from "@ui/lib/utils";
import { useMemo, useState } from "react";

import { Noise } from "@ui/components/core/Noise/Noise";
import { DollyZoomAnimated, type DollyZoomSlide } from "@/app/_components/DollyZoom";
import { OmniboxStateProvider, useOmniboxStateOptional } from "./OmniboxStateContext";
import { type Key, TabPanel, Tabs } from "react-aria-components";
import { TabList } from "@ui/components/core/TabList";
import { AnimatePresence } from "motion/react";

const DEMO_BACKGROUNDS: { default: DollyZoomSlide } & Partial<Record<OmniboxDemo["component"], DollyZoomSlide>> = {
  default: {
    depthMapUrl: "/assets/images/omnibox/home-depth-map.png",
    imageUrl: "/assets/images/omnibox/home@q75.jpg",
    intensity: [0, 1],
    depthInfluence: 0.5,
    focusPoint: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAICAYAAADwdn+XAAABsElEQVR42gXBv08TYRzA4c/3vZPr0VKuaK5iIUdZbKGxEBKNlhgHQdP4JzAwOpmYGKNOLm4OJjoyORqdXFyc/BlNbAwRlbQktLVgLCmlQGl4r6/PI4+uZ7Q9t8TW7BKubePaClsplBIEIb/1msk3T+i2Q3qtXSJRCwRCoL57jNxdyOjc6QSWuPwpPkZ5KSylUCIIggBWr4MoYfrVHRKVz/h+BGUM2zs95N5iTruqT0odM5WeYPXGCuHAECIAAABgjGG8+Y3sy9vsNzs4pk/7KEQeLmR1zGiycSFzKkJl/gEb44sIgggAGAPGGAS4+rxIea3OYBiyua+R+4VAJ5ShkLTJpjy6tQN+5JepXrkJAIABxMC57ifS755R/7pOaibg99ufyNPLgY6L5lIQ40wqyU61Q/XXNsO582xcu8XBaIaTf0tMrL4g2ixjOwoMeFOT1D6sISvzY3rOH8D3HNQJB29khM31Ju1aExGF47lELaFzFDI8OoRfmGEgmUR39mi8LyHL+aCSjkLgwpiEzJ5NEosP8v1jg73WIa5j0RfDYV+YLl7Ev5Cn96+BbrUofSnzH3OYmjL8oTeKAAAAAElFTkSuQmCC",
  },
  stt: {
    depthMapUrl: "/assets/images/omnibox/stt-depth@q75.jpg",
    imageUrl: "/assets/images/omnibox/stt@q75.jpg",
    intensity: [0, 1],
    depthInfluence: 0.5,
    focusPoint: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAICAYAAADwdn+XAAABk0lEQVR42gXBTW7TQACA0W/GnnHi/JGUNFVZUVCRuigL2JVbcCmOAWdAYskGpG4QC2iRqCABpUmVNCSxEns847HNe+Ln5zdeVw9YLQWzTU2vZRmNOmilyPYZcSQYHnfAFny5MXyYamRZsKoVqRTIUmvE1jK9d8wSz8ZqlC/59gdu7hxKOSgE2XKDySAyjqaxDJ2jvffIWGjqGoativOh50kXvk7gepxyNGjSf3hIbTJyUzHfKroIVCnICxDGEopJilSCpwOFb4RcJ4rJ2vPyVPHsMCBfrJGVwW4NseyQtDWzpSDJLQfZCtkgJ3b/iEIHccjvvOL4NObiLGKehvxagEsSZFOxDpv8yDTzQhDsDPJ+SbjUfa5ujziRlpOuo1fXvFto+kHJ5a7H2HV43jigKkru8pJkb0lTx6C2nL/qE36atojiBqN+zuX3v3xMHhH4PW9tmzow5L7g/S7AJIauLVAOAg0XZ1DGEaFoVLzoTQlKh8632HGJao0QccG8kEjvkSHEWUG189gw5PXjjJFec3Ub8B9pNckfOy/f1AAAAABJRU5ErkJggg==",
  },
  "stt-realtime": {
    depthMapUrl: "/assets/images/omnibox/stt-realtime-depth@q75.jpg",
    imageUrl: "/assets/images/omnibox/stt-realtime@q75.jpg",
    intensity: [0, 1],
    depthInfluence: 0.5,
    focusPoint: 0.5,
    placeholder:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAICAYAAADwdn+XAAABlElEQVR42gXBy47SUACA4f+0py0tUBBGcdQ4xmhmodGVutT4Zr6K+9m4cTmL2RkXkhg1Oo5EhIEAhd5vp6d+n7h6/065nsAb9tlKh+mhQ7ZVvHjk4qNQ8Z6ulbNZt1xE95jVJotNiUhSRtECo+fbVEVNmJQMPINVmDP3XAZuwzRxuYgmzKMOUXdEYNhcxgZhpanjDFekyGk25NP2hLfdA+MgQNLlw7pEVhbfaos/jc0t+yFVUmClFXVesYoUb45anj+9jfy+dXg5yTgdpZx/+cfZ6g5jW3Ne9mkJ8ZqajSFokgIyRdtKnk1aXj3ImUUtsvJKelzzddkgm4KT3Zxf5jHH/YKVcDjEOdI2aDOFnWgcU/DED7GLCCvqID/+XhJ7e7a5wWUwpNqXLNOA07sOyu2xDnNoNUYtkAq0MJhdRQx0xviGhbwf/+XzQrHPoU0VWaE4Eh67axclM3paggAXgaMUjqmZp5LNT5/Xj3NkndeQlXTCA2Wh8TG46WmGQrFLTfpIStPCENBoTdkIlCWJtMXZD5//jYXTxWXSXPoAAAAASUVORK5CYII=",
  },
  // Add more demo-specific backgrounds here as needed
  // tts: { ... },
  // dubbing: { ... },
};

interface OmniboxProps {
  childrenBelowDemos?: React.ReactNode;
  className?: string;
  demos?: Array<{
    Component: React.ReactNode;
    component: OmniboxDemo["component"];
    icon?: Media | null | number | string | undefined;
    title: string;
  }>;
  alignToHeroButtons?: boolean;
}

function OmniboxContent({ demos = [], className, childrenBelowDemos, alignToHeroButtons = false }: OmniboxProps) {
  const analytics = useAnalytics();
  const omniboxState = useOmniboxStateOptional();
  const [selectedTab, setSelectedTab] = useState(demos[0]?.component || "tts");

  const selectedIndex = demos.findIndex(demo => demo.component === selectedTab);

  const handleSelection = (key: Key) => {
    setSelectedTab(key as OmniboxDemo["component"]);

    const component = demos.find(demo => demo.component === key)?.component;
    if (component) {
      analytics.captureEvent(EventName.website_clicked_omnibox_tab, {
        tabComponentName: component,
      });
    }
  };

  // TODO: Add this back with a proper crossfade effect

  const isActive = omniboxState?.state === "active";

  const slides = useMemo(
    () => demos.map(demo => DEMO_BACKGROUNDS[demo.component] ?? DEMO_BACKGROUNDS.default),
    [demos],
  );

  const hasSingleDemo = demos.length === 1;
  const wrapperClassName = cn(
    "pointer-events-none relative w-full",
    alignToHeroButtons && "mt-16 lg:-mt-48",
    className,
  );

  const content = (
    <>
      {!hasSingleDemo && (
        <div className='mb-28'>
          <TabList
            items={demos.map(demo => ({ id: demo.component, title: demo.title }))}
            className='pointer-events-auto ml-auto'
          />
        </div>
      )}
      <div className='has-[>[role=tabpanel]:focus-visible]:tw-outline-focus pointer-events-auto relative flex h-[30.5rem] w-full flex-col justify-center overflow-hidden rounded-xl px-16 md:h-[36rem] md:px-52'>
        {/* Placeholder */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=''
          src={slides[selectedIndex].placeholder}
          className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
          inert
        />
        <DollyZoomAnimated slides={slides} selectedIndex={selectedIndex} isActive={isActive} />
        <div className='tw-absolute tw-inset-0 tw-backdrop-blur-[15px]' />
        <Noise inert priority className='tw-opacity-50' />
        <AnimatePresence initial={false} mode='popLayout'>
          {demos.map(({ component, Component }) =>
            component === selectedTab ? (
              hasSingleDemo ? (
                <div key={component} className='relative mx-auto w-full max-w-4xl outline-none'>
                  {Component}
                </div>
              ) : (
                <TabPanel
                  key={component}
                  id={component}
                  shouldForceMount
                  className='relative mx-auto w-full max-w-4xl outline-none'
                >
                  {Component}
                </TabPanel>
              )
            ) : null,
          )}
        </AnimatePresence>
      </div>
      {childrenBelowDemos}
    </>
  );

  if (hasSingleDemo) {
    return <div className={wrapperClassName}>{content}</div>;
  }

  return (
    <Tabs className={wrapperClassName} selectedKey={selectedTab} onSelectionChange={handleSelection}>
      {content}
    </Tabs>
  );
}

export default function OmniboxClientSimple(props: OmniboxProps) {
  return (
    <OmniboxStateProvider>
      <OmniboxContent {...props} />
    </OmniboxStateProvider>
  );
}
